import type { DeviceListResponseV5, DeviceResponseV5 } from '@emma/api_client/types';
import { Box, Button, Flex, Icon, Link, Text } from '@lego/klik-ui';
import { MfgAutostop } from '@lego/klik-ui/icons';
import { useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { DeviceActionScanner, Loader } from '@/components/common';
import type { DeviceScan } from '@/components/common/QrReader';
import { useApiFetch, useMutation } from '@/hooks/api';
import { paths } from '@/utility/global_paths';

export const DeviceCloningView = ({ targetDevice }: { targetDevice: CloneableDevice }) => {
  return (
    <DeviceActionScanner
      instructionText={
        <>
          Enter or scan the EMMA QR code of the <b>source</b> device you wish to clone from
        </>
      }
      title=""
    >
      {(scan, resetScan) => {
        if (targetDevice.serialNumber === scan.serialNumber) {
          return (
            <Box>
              <Text color={'red'} fontSize={'lg'} fontWeight={'medium'} mt={8} textAlign={'center'}>
                <b>Target</b> and <b>source</b> devices cannot be the same
              </Text>
              <Button mt={8} onClick={resetScan} width={'full'}>
                Choose another source
              </Button>
            </Box>
          );
        }

        return <CloningProcessView fromScan={scan} toDevice={targetDevice} />;
      }}
    </DeviceActionScanner>
  );
};

type CloningProcessViewProps = {
  toDevice: CloneableDevice;
  fromScan: DeviceScan;
};

type CloneableDevice = Pick<DeviceResponseV5, 'id' | 'serialNumber' | 'macAddress' | 'workspaceId'>;

const CloningProcessView = ({ toDevice, fromScan }: CloningProcessViewProps) => {
  const searchParams = {
    ...(fromScan.macAddresses.length > 0 && { macAddressLike: fromScan.macAddresses[0] }),
    serialNumberLike: fromScan.serialNumber,
  };

  const fetchAllDevices = async (callApi: Parameters<Parameters<typeof useApiFetch>[1]>[0]) => {
    const devices = await callApi((api) => api.devices.getV5Devices(searchParams));
    return { devicesResponse: devices.data };
  };

  const fetchProvisionedDevices = async (
    callApi: Parameters<Parameters<typeof useApiFetch>[1]>[0]
  ) => {
    const devices = await callApi((api) =>
      api.devices.getV5Devices({ ...searchParams, stateIn: ['PROVISIONED'] })
    );
    return { devicesResponse: devices.data };
  };

  const { data: allData, isLoading: isLoadingAll } = useApiFetch('/from-device', fetchAllDevices);
  const { data: provisionedData, isLoading: isLoadingProvisioned } = useApiFetch(
    '/from-device-provisioned',
    fetchProvisionedDevices
  );

  const renderContent = () => {
    if (isLoadingAll || isLoadingProvisioned || allData === undefined) return <Loader />;
    if (allData.devicesResponse.itemCount === 0) return <DeviceNotRegistered />;
    if (provisionedData === undefined || provisionedData.devicesResponse.itemCount === 0)
      return <DeviceNotProvisioned />;
    return <CloningView fromData={provisionedData} toDevice={toDevice} />;
  };

  return renderContent();
};

type CloningViewProps = {
  toDevice: CloneableDevice;
  fromData: {
    devicesResponse: DeviceListResponseV5;
  };
};

const CloningView = ({ fromData, toDevice }: CloningViewProps) => {
  const [isCloning, setIsCloning] = useState<boolean>(false);
  const [isCloned, setIsCloned] = useState<boolean>(false);

  const fromDevice = fromData.devicesResponse.devices[0];

  const cloneMutation = useMutation('device-clone', (client) =>
    client.v1.workspaces[':workspaceId'].device.clone.$post({
      param: { workspaceId: toDevice.workspaceId.toString() },
      json: {
        source: {
          macAddresses: [fromDevice.macAddress],
          serialNumber: fromDevice.serialNumber,
        },
        target: {
          macAddresses: [toDevice.macAddress],
          serialNumber: toDevice.serialNumber,
        },
      },
    })
  );

  const clone = async () => {
    setIsCloning(true);
    try {
      await cloneMutation();
      setIsCloned(true);
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <Box>
      <Text fontSize={'lg'} fontWeight={'medium'} mt={8} textAlign={'center'}>
        Cloning from{' '}
        <Link as={ReactRouterLink} p={0} to={paths.deviceById(fromDevice.id)}>
          {fromDevice.serialNumber}
        </Link>{' '}
        to{' '}
        <Link as={ReactRouterLink} p={0} to={paths.deviceById(toDevice.id)}>
          {toDevice.serialNumber}
        </Link>
      </Text>

      <Button isDisabled={isCloning} mt={8} onClick={clone} width={'full'}>
        Clone
      </Button>
      {isCloned && (
        <Text color={'green'} fontSize={'lg'} fontWeight={'medium'} mt={8} textAlign={'center'}>
          Device has been successfully cloned.{' '}
          <Link as={ReactRouterLink} p={0} to={paths.deviceById(toDevice.id)}>
            View cloned device
          </Link>
        </Text>
      )}
      {isCloning && <Loader />}
    </Box>
  );
};

const DeviceNotRegistered = () => {
  return (
    <Box>
      <Flex alignItems={'center'} justifyContent={'center'}>
        <Icon as={MfgAutostop} color={'red'} fontSize={'200'} />
      </Flex>
      <Text fontSize={'lg'} fontWeight={'medium'} mt={6} textAlign={'center'}>
        Device is not yet registered in EMMA. Please register your device before assigning it to a
        fleet.
      </Text>
    </Box>
  );
};

const DeviceNotProvisioned = () => {
  return (
    <Box>
      <Flex alignItems={'center'} justifyContent={'center'}>
        <Icon as={MfgAutostop} color={'red'} fontSize={'200'} />
      </Flex>
      <Text fontSize={'lg'} fontWeight={'medium'} mt={6} textAlign={'center'}>
        Device is not yet provisioned in EMMA. Please provision your device before attempting to use
        it as a clone.
      </Text>
    </Box>
  );
};
